/**
 * ChangelogPage.tsx — ɳSentry roadmap / changelog
 *
 * Purpose:    Pre-launch roadmap; becomes release changelog post-v1.1.0 ship
 * Inputs:     none (static data)
 * Outputs:    Roadmap list with version/status/items
 * SOT:        T-P3-E3-W2-S5-T01
 */
import { Link } from 'react-router-dom'

const roadmap = [
  {
    version: 'v1.2.0',
    label: 'Expansion and integrations',
    status: 'planned' as const,
    date: 'TBD',
    items: [
      'nself-errors: error tracking with release attribution',
      'nself-cron-monitor: missed-run detection',
      'nself-oncall: on-call rotation management',
      'nself-crash: mobile and desktop crash reporting',
      'nself-anomaly: anomaly detection on metrics',
      'Additional alert channel integrations',
      'Multi-region status page support',
    ],
  },
  {
    version: 'v1.1.0',
    label: 'Baseline bundle ships',
    status: 'planned' as const,
    date: 'TBD',
    items: [
      'nself-uptime-monitor: HTTP/TCP uptime checks',
      'nself-status-page: public and private status pages',
      'nself-incident-mgmt: incident lifecycle and post-mortems',
      'nself-alert-router: multi-channel alerting (Slack, email, webhooks)',
      'nself-slo-tracker: SLO compliance and error budgets',
      'nself-synthetic-monitor: scripted browser uptime',
      'nself-rum: real-user monitoring and Core Web Vitals',
      'nself-audit: audit log (always free)',
      'ɳSentry Bundle pricing locked at $0.99/mo',
      'Bundle plugins listed on nself.org',
      'sentry.nself.org marketing site (this site)',
      'Waitlist open',
    ],
  },
]

const statusLabel: Record<'planned' | 'done' | 'in-progress', string> = {
  planned: 'Planned',
  done: 'Released',
  'in-progress': 'In progress',
}

const statusColor: Record<'planned' | 'done' | 'in-progress', string> = {
  planned: 'text-brand-muted border-brand-card bg-brand-surface/30',
  done: 'text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/50 bg-emerald-50 dark:bg-emerald-950/20',
  'in-progress': 'text-indigo-700 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800/50 bg-indigo-50 dark:bg-indigo-950/20',
}

export function ChangelogPage() {
  return (
    <>
      <section className="mx-auto max-w-4xl px-4 py-20 sm:px-6 lg:px-8 text-center">
        <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-indigo-200 dark:border-indigo-800/50 bg-indigo-50 dark:bg-indigo-950/50 px-3 py-1 text-sm text-indigo-700 dark:text-indigo-300">
          Pre-launch
        </div>
        <h1 className="mb-4 text-4xl font-bold sm:text-5xl">Roadmap</h1>
        <p className="text-lg text-brand-muted">
          ɳSentry is in active development. This page tracks what is planned and when it ships. Post-launch it becomes a release changelog.
        </p>
      </section>

      <section className="border-t border-brand-subtle py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {roadmap.map((r) => (
              <div key={r.version} className="grid gap-6 sm:grid-cols-[12rem_1fr]">
                <div>
                  <p className="font-mono text-sm font-bold text-brand-ink">{r.version}</p>
                  <p className="mt-1 text-sm text-brand-muted">{r.label}</p>
                  <p className="mt-1 text-xs text-brand-muted">{r.date}</p>
                  <span className={`mt-2 inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${statusColor[r.status]}`}>
                    {statusLabel[r.status]}
                  </span>
                </div>
                <ul className="space-y-2">
                  {r.items.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-brand-secondary">
                      <svg className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-muted" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-brand-subtle bg-brand-surface/30 py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="mb-4 text-2xl font-bold">Get notified at launch</h2>
          <p className="mb-8 text-brand-muted">Join the waitlist for an invite code and pricing notification when ɳSentry ships in v1.1.0.</p>
          <Link to="/invite" className="rounded-md bg-sky-500 px-8 py-3 text-base font-medium text-white shadow-lg hover:bg-sky-400 transition-colors">
            Join the waitlist
          </Link>
        </div>
      </section>
    </>
  )
}
